import React from 'react';
import { get, isEmpty } from 'lodash';
import { withRouter } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

// local dependencies
import { transferQueue as TransferQueue } from 'ui/views/widgets/transferQueue';
import { TransferQueueWidgetService } from 'services/transferQueueWidget';
import * as confirmOverlay from 'utils/confirmOverlay';
import { getLabel } from 'utils/getLabel';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import { initCallState } from 'store/actions/callState';
import { getRedirectionRoute } from 'resolvers/callStateEntities';

import { REDUCER } from 'constants/entity-names';
import { SERVICE_RESPONSE_STATUS, SIZES } from 'constants/common';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { TransferQueueWidget } from 'ui/containers/TransferQueueWidget';
 *
 * // render
 * <TransferQueueWidget propName='propValue'/>
 */
export class _TransferQueueWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );
        
        // create service instance
        this.transferQueueWidgetService = new TransferQueueWidgetService();

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
        };

        // bind functions
        this.handleActionClick = this.handleActionClick.bind( this );

        // start data fetch
        this.fetchComponentData();

    }

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'TransferQueueWidget.fetchComponentData()' );

        // cancel ongoing request
        this.transferQueueWidgetService.cancel();

        // fetch component data
        const { status, data } = await this.transferQueueWidgetService.fetchComponentData(  {
            url: this.props.widget.url,
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
            } );
        }
    }


    /**
     * @desc Action handler for column action and group action.
     */
    handleActionClick( { type = 'column', value = {}, item = {} } = {}, callback ) {
        log.debug( 'TransferQueueWidget.handleActionClick()' );

        if( 'groupAction' === type && 'takeTransfer' === value.name ) {
            return this.initiateTransferCall( {
                action: value,
                url: value.url,
                payload: {
                    id: item.id,
                },
            } );
        }

        if( 'groupAction' === type && 'remove' === value.name ) {
            return this.initiateRemoveCallFromTransferQueue( {
                action: value,
                url: value.url,
                payload: {
                    id: item.id,
                },
            }, callback );
        }
    }

    async initiateTransferCall( { action, url, payload } ) {
        log.debug( 'TransferQueueWidget.initiateTransferCall()' );

        // get confirmation title
        const message = get( action, 'confirmation.message' );

        const shouldProceed = await confirmOverlay.open( { message: getLabel( message ) } );

        if( shouldProceed ) {

            // cancel ongoing request
            this.transferQueueWidgetService.cancel();

            // show page level preloader
            pageLevelPreloader.show();

            // fetch component data
            const { status, data } = await this.transferQueueWidgetService.transferCall( {
                url: url,
                data: payload
            } );

            // hide page level preloader
            pageLevelPreloader.hide();

            // check if response status was success
            if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                }, () => {
                    
                    // initiate call state
                    this.props.initCallState( data );

                    // change to authorization flow route
                    const redirectionPath = getRedirectionRoute( data );
                    if( ! isEmpty( redirectionPath ) ) {
                        this.props.history.push( redirectionPath );
                    }

                } );
            } else {

                // show error dialog
                if( ! isEmpty( data.description ) ) {
                    errorOverlay.open( { message: data.description, size: SIZES.SMALL, } );
                } else {
                    errorOverlay.open();
                }
            }

        }
    }

    async initiateRemoveCallFromTransferQueue( { action, url, payload }, callback ) {
        log.debug( 'TransferQueueWidget.initiateRemoveCallFromTransferQueue()' );

        // get confirmation title
        const title = get( action, 'confirmation.title' );

        // get confirmation title
        const message = get( action, 'confirmation.message' );

        const shouldProceed = await confirmOverlay.open( { title: getLabel( title ), message: getLabel( message ), iconName: 'close' } );

        if( shouldProceed ) {

            // cancel ongoing request
            this.transferQueueWidgetService.cancel();

            // show page level preloader
            pageLevelPreloader.show();

            // fetch component data
            const { status } = await this.transferQueueWidgetService.removeCallFromQueue( {
                url: url,
                data: payload
            } );

            // hide page level preloader
            pageLevelPreloader.hide();

            // check if response status was success
            if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                }, () => {

                    const { id } = payload;
                    
                    callback( { id } );
                    
                    // this.removeCallFromTransferQueue( payload );
                } );
            } else {
                errorOverlay.open(); // show error
            }

        }
    }

    // removeCallFromTransferQueue( payload ) {
    //     log.debug( 'TransferQueueWidget.removeCallFromTransferQueue()' );

    //     // id to be removed
    //     const { id } = payload;

    //     // get the items list from state
    //     const componentData = cloneDeep( this.state.componentData );
    //     const items = get( componentData, 'data.state.items', [] );

    //     componentData.data.state.items = filter( items, item => {
    //         return id !== item.id;
    //     } );

    //     this.setState( { componentData: componentData } );

    // }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        log.debug( 'TransferQueueWidget.componentWillUnmount()' );

        this.transferQueueWidgetService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <TransferQueue
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                componentData={ get( this.state, 'componentData', {} ) }
                onActionClick={ this.handleActionClick }
            />
        );
    }
}

// set display name
_TransferQueueWidget.displayName = 'TransferQueueWidget';

// set default props
_TransferQueueWidget.defaultProps = {};

// prop types
_TransferQueueWidget.propTypes = {};

/*************************************************/

// map state to props
const mapStateToProps = ( state ) => {
    return {
        callState: state[ REDUCER.CALL_STATE ]
    };
};

// map dispatch to props
const mapDispatchToProps = ( dispatch ) => {
    return {
        initCallState: bindActionCreators( initCallState, dispatch ),
    };
};

// return component with acccess to Redux store
export const TransferQueueWidget = withRouter( connect( mapStateToProps, mapDispatchToProps )( _TransferQueueWidget ) );
